import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useModalConfirmActionStore } from "../../../stores/admin/modalConfirmActionStore";
import OfflineService from "../../../api/admin/offlineService";
import useOfflineServiceStore from "../../../stores/admin/offlineServiceStore";
import {
  ORDER_STATUS,
  OrderStatus,
} from "../../common/orderStatus/status.constants";
import { getOrderStatusName } from "../../common/orderStatus/getOrderStatusName";

interface AdminUpdateStateProps {
  fileId: number;
  status: number;
}

const AdminUpdateState: React.FC<AdminUpdateStateProps> = ({
  fileId,
  status,
}) => {
  const { setMessage, setModalOpen, setConfirmAction } =
    useModalConfirmActionStore();
  const { t } = useTranslation();
  const { order, fetchOrder } = useOfflineServiceStore();

  function setOrderStatus(id: number, status: OrderStatus): void {
    setMessage(
      `${t("Are you sure you want to change status to")} '${t(
        getOrderStatusName(status)
      )}' ${id}?`
    );
    setConfirmAction(async () => {
      const offlineService = new OfflineService();
      await offlineService.changeOrderStatus(id, status);
      await fetchOrder(id);
    });
    setModalOpen(true);
  }

  const RejectButton = (
    <button
      className="flex items-center justify-center border-danger
                        border border-solid rounded-lg px-3 cursor-pointer bg-danger h-10 w-38 my-2"
      onClick={() => setOrderStatus(fileId, ORDER_STATUS.CANCELED)}
    >
      {t(getOrderStatusName(ORDER_STATUS.CANCELED))}
    </button>
  );

  const UnderReviewButton = (
    <button
      className="flex items-center justify-center border-white text-black
                        border border-solid rounded-lg px-3 cursor-pointer bg-white h-10 w-38 my-2"
      onClick={() => setOrderStatus(fileId, ORDER_STATUS.UNDER_REVIEW)}
    >
      {t(getOrderStatusName(ORDER_STATUS.UNDER_REVIEW))}
    </button>
  );

  const ProcessingButton = (
    <button
      className="flex items-center justify-center border-primary
                        border border-solid rounded-lg px-3 cursor-pointer bg-primary h-10 w-38 my-2"
      onClick={() => setOrderStatus(fileId, ORDER_STATUS.PROCESSING)}
    >
      {t(getOrderStatusName(ORDER_STATUS.PROCESSING))}
    </button>
  );

  const RevisionInProgressButton = (
    <button
      className="flex items-center justify-center border-orange-600
                        border border-solid rounded-lg px-3 cursor-pointer bg-orange-600 h-10 w-38 my-2"
      onClick={() => setOrderStatus(fileId, ORDER_STATUS.REVISION_IN_PROGRESS)}
    >
      {t(getOrderStatusName(ORDER_STATUS.REVISION_IN_PROGRESS))}
    </button>
  );

  const CompletedButton = (
    <button
      className="flex items-center justify-center border-success
                        border border-solid rounded-lg px-3 cursor-pointer bg-success h-10 w-38 my-2"
      onClick={() => setOrderStatus(fileId, ORDER_STATUS.COMPLETED)}
    >
      {t(getOrderStatusName(ORDER_STATUS.COMPLETED))}
    </button>
  );

  const ProcessedStatus = (
    <>
      {UnderReviewButton}
      {RejectButton}
    </>
  );
  const CanceledStatus = <>{UnderReviewButton}</>;
  const UnderReviewStatus = (
    <>
      {ProcessingButton}
      {RevisionInProgressButton}
      {RejectButton}
    </>
  );
  const ProcessingStatus = (
    <>
      {CompletedButton}
      {RevisionInProgressButton}
      {RejectButton}
    </>
  );
  const RevisionInProgressStatus = (
    <>
      {ProcessingButton}
      {RejectButton}
    </>
  );
  const RejectedStatus = (
    <>
      {UnderReviewButton}
    </>
  );
  const CompletedStatus = (
    <>
      {UnderReviewButton}
      {ProcessingButton}
      {RejectButton}
    </>
  );
  const DefaultStatus = (
    <>
      {CompletedButton}
      {RevisionInProgressButton}
      {RejectButton}
    </>
  );

  return (() => {
    switch (status) {
      case 3:
      case 5:
        return ProcessedStatus;
      case 6:
      case 7:
        return CanceledStatus;
      case 8:
        return UnderReviewStatus;
      case 9:
        return ProcessingStatus;
      case 10:
        return RevisionInProgressStatus;
      case 11:
        return RejectedStatus;
      case 12:
        return CompletedStatus;
      default:
        return DefaultStatus;
    }
  })();
};

export default AdminUpdateState;
